"use client";

/**
 * Motion system foundation (build-prompt Phase 4) — reduced-motion helpers.
 *
 * Single source of truth for `prefers-reduced-motion` checks used by every
 * motion hook (useReveal / useCountUp / useStageDriver). All animation in
 * the app is gated behind this preference: when it is set, hooks render the
 * COMPLETED end state instead of animating (per Global rule 5).
 */

import { useSyncExternalStore } from "react";

const QUERY = "(prefers-reduced-motion: reduce)";

/**
 * Non-hook check, safe to call anywhere (returns false during SSR).
 * Prefer `usePrefersReducedMotion()` inside components so the value is
 * reactive when the OS setting changes mid-session.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(QUERY).matches;
}

function subscribe(onStoreChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => {};
  }
  const mql = window.matchMedia(QUERY);
  mql.addEventListener("change", onStoreChange);
  return () => mql.removeEventListener("change", onStoreChange);
}

/**
 * Reactive `prefers-reduced-motion` hook.
 * SSR snapshot is `false` (no motion runs on the server anyway; hooks only
 * animate from effects, so there is no hydration flash).
 */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, prefersReducedMotion, () => false);
}
